import React, { Component } from "react";
import { DataTableToolbar } from "../../components/ui";

import _ from "lodash";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import { DataTableSortMeta } from "primereact/datatable";

import {
    AllTransactionRequestModel,
    PaymentStatus,
    TransactionReportResponse,
    Transaction,
    TransactionReportDetails,
    TransactionStatus,
} from "../../models/Transactions";
import { dateRangeFilterTemplate, statusFilterMatchModeOptions, stringFilterMatchModeOptions } from "../Paginator";
import { toastService } from "../../services/toastService";
import { Dropdown } from "primereact/dropdown";
import { SelectItem } from "../../models/SelectItem";
import { Calendar } from "primereact/calendar";
import {
    RenderStatusBadge,
    TransactionTypeFilterTemplate,
} from "../Operations/TransactionFilterTemplates";
import { transactionsReportApiService } from "../../services/api/operations/TransactionsReportApiService";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import { utils } from "../../utils/utils";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { currenciesApiService } from "../../services/api/merchants/CurrenciesApiService";
import TransactionDetailsDialog from "../Users/TransactionDetails";
import TransactionStatusHelper from "../Operations/TransactionStatusHelper";
import classNames from "classnames";

interface TransactionsState {
    isLoading: boolean;
    transactions: TransactionReportDetails[];
    first: number;
    rows: number;
    filters?: SearchRequestBaseModel;
    multiSortMeta: DataTableSortMeta[];
    isTransactionDetailVisible: boolean;
    selectedTransaction: TransactionReportDetails | null;
    currencies: SelectItem[];
    totalRecords: number;
}

interface TransactionProps {
    merchantId: number | undefined;
    customerId: string | undefined;
}

export default class MerchantTransactions extends Component<TransactionProps, TransactionsState> {
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: TransactionProps | Readonly<TransactionProps>) {
        super(props);

        this.state = {
            isLoading: true,
            transactions: [],
            multiSortMeta: [],
            first: 0,
            rows: 10,
            isTransactionDetailVisible: false,
            selectedTransaction: null,
            currencies: [],
            totalRecords: 0,
        };
    }

    async componentDidMount() {
        await Promise.all([this.loadCurrencies(), this.loadTransactions({} as SearchRequestBaseModel)]);
        this.setState({ isLoading: false });
    }

    loadCurrencies = async () => {
        const currenciesResult = await currenciesApiService.getMerchantActiveCurrencies(this.props.merchantId!);
        if (!currenciesResult.success) {
            toastService.error(currenciesResult.message);
            this.setState({ isLoading: false });
        }
        const currenciesList: SelectItem[] = currenciesResult.data.map((currency) => ({
            label: currency.charCode,
            value: currency.charCode,
        }));

        this.setState({ currencies: currenciesList });
    };

    loadTransactions = async (filters: SearchRequestBaseModel) => {
        const filterRequest: AllTransactionRequestModel = await utils.extractFiltersData(filters);
        if (this.props.customerId) {
            filterRequest.customerID = this.props.customerId;
            filterRequest.dataBatch = filters.pageNumber ?? 1;
            filterRequest.dataBatchCount = filters.pageSize ?? 10;
            this.setState({ isLoading: true });

            const transactionResult = await transactionsReportApiService.getAllTransactionsReportResponse(filterRequest);

            if (!transactionResult.success) {
                toastService.error(transactionResult.message);
                this.setState({ isLoading: false });
                return;
            }
            this.setState({
                transactions: transactionResult.data.transactions,
                totalRecords: transactionResult.data.transactionCount,
                isLoading: false,
            });
        }
    };

    currencyFilterTemplate = (options: ColumnFilterElementTemplateOptions, columnName: string, placeHolder: string) => {
        return (
            <Dropdown
                value={options.value}
                options={this.state.currencies}
                filter
                onChange={(e) => options.filterCallback(e.value, options.index)}
                placeholder={`Select ${placeHolder}`}
                className="p-column-filter"
                showClear
            />
        );
    };

    dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <Calendar
                value={options.value}
                onChange={(e) => options.filterCallback(e.value, options.index)}
                dateFormat="mm/dd/yy"
                placeholder="mm/dd/yyyy"
                mask="99/99/9999"
            />
        );
    };

    showTransactionDetails = (rowData: TransactionReportDetails) => {
        this.setState({ isTransactionDetailVisible: true, selectedTransaction: rowData });
    };

    renderHeader = (hasActiveFilters: boolean) => <DataTableToolbar onClear={() => this.dataTable!.reset()} hasActiveFilters={hasActiveFilters} />;

    renderStatusBody = (rowData: TransactionReportDetails) => {

        return RenderStatusBadge(rowData.transactionStatus);
    };

    render() {
        return (
            <div>
                {this.state.isTransactionDetailVisible && this.state.selectedTransaction != null && (
                    <TransactionDetailsDialog
                        isVisible={this.state.isTransactionDetailVisible}
                        onHide={() => this.setState({ isTransactionDetailVisible: false })}
                        onRollbackSuccess={() => this.loadTransactions({} as SearchRequestBaseModel)}
                        transaction={this.state.selectedTransaction}
                        editable={false}
                    />
                )}

                <div>

                    <ExtendedDataTable
                        header={this.renderHeader}
                        value={this.state.transactions}
                        loading={this.state.isLoading}
                        totalRecords={this.state.totalRecords}
                        onChange={this.loadTransactions}
                        ref={(ref) => (this.dataTable = ref)}
                        defaultSortOrder={SortOrderType.Descending}
                        defaultSortField="timeStamp"
                        onRowClick={(e) => this.showTransactionDetails(e.data as TransactionReportDetails)}
                    >
                        <Column
                            {...({ alwaysVisible: true } as any)}
                            field="type"
                            header="Type"
                            body={(rowData: TransactionReportDetails) => <p>{rowData.transactionType}</p>}
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                            showFilterMatchModes={false}
                            filterField="trxDirection"
                            filterElement={TransactionTypeFilterTemplate}
                        ></Column>
                        <Column
                            {...({ alwaysVisible: true } as any)}
                            field="walletId"
                            header="Wallet Id"
                            body={(rowData: TransactionReportDetails) => <p>{rowData.accountNumber}</p>}
                            //filterElement={(options => this.transactionTypeFilterTemplate(options, 'walletId', 'Wallet Id'))}
                            showFilterMatchModes={false}
                            filterField="accountID"
                            filterPlaceholder="Search by Wallet ID"
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                        />
                        <Column
                            {...({ alwaysVisible: true } as any)}
                            field="timeStamp"
                            header="Transaction Date"
                            body={dateBodyTemplate("timeStamp")}
                            dataType="date"
                            filterElement={dateRangeFilterTemplate}
                            filterField="timeStampTo"
                            filterMatchModeOptions={statusFilterMatchModeOptions}
                            showFilterMatchModes={false}
                        ></Column>
                        <Column
                            field="amount"
                            header="Amount"
                            showFilterMatchModes={false}
                            filterField="amountTo"
                            filterPlaceholder="Search by Amount"
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                            body={(rowData: TransactionReportDetails) => <p>{utils.renderAmount(rowData.amount)}</p>}
                        />
                        <Column
                            field="currency"
                            header="Currency"
                            body={(rowData: TransactionReportDetails) => <p>{rowData.currency}</p>}
                            showFilterMatchModes={false}
                            filterField="currency"
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                            filterElement={(options) => this.currencyFilterTemplate(options, "currency", "Currency")}
                        ></Column>
                        <Column
                            field="id"
                            header="Reference Number"
                            body={(rowData: TransactionReportDetails) => <p>{rowData.transactionId}</p>}
                            showFilterMatchModes={false}
                            filterField="transactionID"
                            filterPlaceholder="Search by Transaction ID"
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                        //filterElement={(options => this.transactionTypeFilterTemplate(options, 'id', 'Reference Number'))}
                        ></Column>
                        <Column
                            field="status"
                            header="Status"
                            body={this.renderStatusBody}
                        // filterElement={(options => this.transactionTypeFilterTemplate(options, 'status', 'Status'))}
                        ></Column>
                    </ExtendedDataTable>

                </div>
            </div>
        );
    }
}
